import React, { useState } from 'react';
import axios from 'axios';
import { Snowflake, Wallet, AlertCircle, CreditCard, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

export const CRYSTAL_PACK_DISPLAY = [
  { id: 'pack_500',  crystals: 500,  price_eur: 5,  label: 'Iniciado' },
  { id: 'pack_1200', crystals: 1200, price_eur: 10, label: 'Aventurero' },
  { id: 'pack_2500', crystals: 2500, price_eur: 20, label: 'Heroe' },
  { id: 'pack_5000', crystals: 5000, price_eur: 40, label: 'Campeon' },
];

export default function BuyCrystalsModal({ open, onClose, user, crystals }) {
  const [loading, setLoading] = useState(null);
  const [error, setError]     = useState('');
  const [custom, setCustom]   = useState('');
  const [tab, setTab]         = useState('packs');

  if (!open) return null;

  const handleBuyPack = async (packId) => {
    if (!user) { window.location.href = '/login'; return; }
    setLoading(packId); setError('');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payment/checkout`,
        { pack_id: packId },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail ?? 'Error al iniciar el pago. Inténtalo de nuevo.');
      setLoading(null);
    }
  };

  const handleBuyCustom = async () => {
    const amount = parseInt(custom, 10);
    if (!amount || amount < 100) { setError('El mínimo son 100 crystals.'); return; }
    if (amount % 100 !== 0) { setError('La cantidad debe ser múltiplo de 100.'); return; }
    if (!user) { window.location.href = '/login'; return; }
    setLoading('custom'); setError('');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payment/checkout`,
        { crystals: amount },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail ?? 'Error al iniciar el pago. Inténtalo de nuevo.');
      setLoading(null);
    }
  };

  const customAmount = parseInt(custom, 10) || 0;
  const customEur    = customAmount >= 100 ? (customAmount / 100).toFixed(2) : '—';

  return (
    <div
      role="presentation"
      className="fixed inset-0 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.75)', zIndex: 10000 }}
      onClick={onClose}
      onKeyDown={() => {}}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-lg mx-4 rounded-2xl p-6"
        style={{ background: 'rgba(8,12,28,0.97)', border: '1px solid rgba(0,180,255,0.3)', boxShadow: '0 0 40px rgba(0,120,200,0.2)', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
          style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
        >
          ✕
        </button>

        <div className="flex items-center gap-3 mb-1">
          <Snowflake size={24} className="text-cyan-400" />
          <h2 className="text-white font-bold text-lg">Comprar Cristales de Escarcha</h2>
        </div>
        <p className="text-slate-400 text-sm mb-1">
          Los cristales son permanentes y se usan en la tienda.
        </p>
        <div className="flex items-center gap-2 mb-4 text-xs px-3 py-1.5 rounded-lg w-fit"
          style={{ background: 'rgba(0,180,255,0.07)', border: '1px solid rgba(0,180,255,0.2)', color: 'rgba(0,229,255,0.8)' }}>
          <Snowflake size={11} />
          <span>1 € = 100 Cristales de Escarcha</span>
        </div>

        {user && (
          <div className="flex items-center gap-2 mb-5">
            <Wallet size={16} className="text-cyan-400" />
            <span className="text-slate-300 text-sm">Tus cristales actuales: <strong className="text-cyan-300">{crystals?.toLocaleString() || 0}</strong></span>
          </div>
        )}

        <div className="flex gap-2 mb-4">
          {['packs', 'custom'].map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setError(''); }}
              className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all"
              style={{
                background: tab === t ? 'rgba(0,150,220,0.25)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${tab === t ? 'rgba(0,229,255,0.5)' : 'rgba(255,255,255,0.1)'}`,
                color: tab === t ? 'rgba(0,229,255,0.95)' : 'rgba(255,255,255,0.4)',
              }}
            >
              {t === 'packs' ? 'Packs' : 'Cantidad personalizada'}
            </button>
          ))}
        </div>

        {tab === 'packs' ? (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {CRYSTAL_PACK_DISPLAY.map(pack => (
              <button
                key={pack.id}
                onClick={() => handleBuyPack(pack.id)}
                disabled={!!loading}
                className="flex flex-col items-center gap-1.5 p-4 rounded-xl transition-all hover:brightness-125 active:scale-95 disabled:opacity-60"
                style={{ background: 'rgba(0,150,220,0.1)', border: '1px solid rgba(0,180,255,0.25)' }}
              >
                <Snowflake size={20} className="text-cyan-400" />
                <span className="text-white font-bold text-lg">{pack.crystals.toLocaleString()}</span>
                <span className="text-cyan-300 text-xs font-semibold">{pack.label}</span>
                <span className="text-slate-400 text-xs">{pack.price_eur} €</span>
                {loading === pack.id && <Loader2 size={14} className="animate-spin text-cyan-400 mt-1" />}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 input-wrap" style={{ border: '1px solid rgba(0,180,255,0.3)', borderRadius: 8 }}>
                <Snowflake size={15} className="input-icon text-cyan-400" />
                <input
                  type="number"
                  min={100}
                  step={100}
                  value={custom}
                  onChange={e => { setCustom(e.target.value); setError(''); }}
                  placeholder="Cantidad de crystals (min. 100)"
                  className="form-input"
                  style={{ background: 'transparent' }}
                />
              </div>
            </div>
            {customAmount >= 100 && (
              <p className="text-sm text-center" style={{ color: 'rgba(0,229,255,0.8)' }}>
                {customAmount.toLocaleString()} crystals = <strong>{customEur} €</strong>
              </p>
            )}
            <button
              onClick={handleBuyCustom}
              disabled={!!loading || customAmount < 100}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all hover:brightness-125 active:scale-95 disabled:opacity-50"
              style={{ background: 'rgba(0,150,220,0.2)', border: '1px solid rgba(0,229,255,0.4)', color: '#fff' }}
            >
              {loading === 'custom' ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              {loading === 'custom' ? 'Procesando...' : 'Comprar'}
            </button>
            <p className="text-xs text-center text-slate-500">Múltiplos de 100. Mínimo 100 crystals (1 €).</p>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-xs text-red-400 mb-3 px-1">
            <AlertCircle size={13} /> {error}
          </div>
        )}

        <p className="text-center text-xs text-slate-500">
          Pago seguro via Stripe. No reembolsable. Al comprar aceptas los{' '}
          <a href="/terminos" className="text-cyan-400 hover:text-cyan-300">Terminos de Servicio</a>.
        </p>
      </div>
    </div>
  );
}
